import {createContext, useMemo, useState, useEffect, useCallback, useContext, useRef} from "react";
import { useQueryClient } from '@tanstack/react-query';
// import {persistState, retrieveCachedObject, cacheObject} from "../lib/utils";
import { useAccountsQuery, useBalancesQuery, useOrdersQuery, usePositionsQuery, useNetWorthQuery, queryKeys } from '../hooks/useTradeStationQueries';
import { AuthContext } from './AuthContext';
import { BannerContext } from './BannerContext';
import { useUpdateAppSettings } from '../hooks/useBackend';
import { usePositionsManager } from '../hooks/usePositionsManager';
import { useIsMobile } from '../hooks/useIsMobile';
import { useOrdersManager } from '../hooks/useOrdersManager';
import { normalizeBrokerOrders } from '../utils/brokerNumericFormat';
import {
  resolveBrokerConnectionForAccount,
  brokerConnectionsEqual,
  hasResolvableBrokerIdentity,
} from '../utils/brokerConnection';
import { findStarredAccount, pickPreferredAccount } from '../utils/starredAccount';
import { rundownAccountKey } from '../utils/rundownAccounts';
import { retireLinkedOrder, sealTerminalOrder } from '../utils/orderReplace';

const defaultUseTrading = {
    balances: [],
    // Ephemeral defaults; do not read from localStorage for TradeStation Customer Data
    selectedAccount: null,
    // NOTE: selectedTicker removed - get it from AppStateContext instead
    selectedTickerDetails: null,
    isPaperTrading: true,
    currentStopLossOrders: null,
    netWorth: null,
    netWorthBreakdown: [],
    netWorthLoading: false,
    hasLiveAccounts: false,
    // Loading states
    balancesLoading: false,
    ordersLoading: false,
    positionsLoading: false,
    isAccountDataLoading: false
}

export const useTradingContext = () => {
    const { user, updateUserSettings } = useContext(AuthContext);
    const { showMessage } = useContext(BannerContext);
    
    const queryClient = useQueryClient();

    // Centralized position state management
    const positionsManager = usePositionsManager();
    
    // Raw state variables
    const [balances, setBalances] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(defaultUseTrading['selectedAccount']);
    const [selectedTickerDetails, setSelectedTickerDetails] = useState(defaultUseTrading['selectedTickerDetails']);
    const [isPaperTrading, setIsPaperTrading] = useState(defaultUseTrading['isPaperTrading']);
    const [currentStopLossOrders, setCurrentStopLossOrders] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [orders, setOrdersRaw] = useState([]);
    const setOrders = useCallback((updater) => {
        setOrdersRaw((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (!Array.isArray(next)) return next;
            return normalizeBrokerOrders(next);
        });
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [tickers, setTickers] = useState([]);

    // React Query hooks
    const accountsQuery = useAccountsQuery(isPaperTrading);
    const { updateAppSettings } = useUpdateAppSettings();

    // Stable refs so callbacks always read the latest auth values without taking them as deps.
    // This prevents setSelectedAccountPersist from being recreated on every render due to
    // object-identity churn on user?.appSettings.
    const userRef = useRef(user);
    useEffect(() => { userRef.current = user; }, [user]);

    const updateUserSettingsRef = useRef(updateUserSettings);
    useEffect(() => { updateUserSettingsRef.current = updateUserSettings; }, [updateUserSettings]);

    // Check if user is superuser
    const isSuperuser = user?.superuser || false;
    
    // NOTE: selectedTicker has been moved to AppStateContext
    // Components should get it from there instead of TradingContext

    // Custom paper trading setter with validation + server persistence
    const setPersistedIsPaperTrading = useCallback((value) => {
        if (!isSuperuser && !value) {
            console.warn('Non-superusers cannot switch to live trading');
            showMessage('Live trading is not enabled currently. Please sign up to our mailing list for updates.', 'warning');
            return;
        }
        setIsPaperTrading(value);
        try {
            if (user?.token) {
                updateAppSettings(user.token, { isPaperTrading: value });
            }
        } catch (_) {}
    }, [isSuperuser, showMessage, user?.token, updateAppSettings]);

    // Bootstrap selectedAccount from user settings only after accounts are known, so we never
    // hit CT_ACCOUNT_NOT_FOUND with a stale SIM id left over from a prior connection.
    useEffect(() => {
        const brokerLinked = user?.hasBrokerConnection || user?.hasTradeStationCredentials;
        if (!brokerLinked) {
            if (selectedAccount) {
                setSelectedAccount(null);
            }
            return;
        }
        // Early bootstrap deferred to the accounts-load effect below (validates preferredId).
    }, [user?.hasBrokerConnection, user?.hasTradeStationCredentials, selectedAccount?.AccountID, setSelectedAccount]);

    // Accounts loading via React Query (backend JWT based)
    // Demo users: only use paper trading (SIM) accounts - filter out any live accounts
    const isDemo = user?.is_demo === true;
    useEffect(() => {
        if (!user?.token) return;
        setIsLoading(accountsQuery.isLoading);
        // While the first accounts fetch is in flight, keep any existing selection — clearing
        // here (empty list) oscillates AccountID null↔value and storms trading_rules_config /
        // trading_hours_restrictions/status.
        if (accountsQuery.isLoading && !accountsQuery.data) {
            return;
        }
        const rawAccounts = accountsQuery.data?.Accounts || [];
        let accountsList = rawAccounts;
        if (isDemo) {
            accountsList = accountsList.filter(acc => acc.isPaperTrading === true);
        }
        setAccounts(accountsList);

        let appSettings = { ...(user?.appSettings || {}) };
        const starredId = appSettings.starredAccountId;
        // Only drop a star after a successful account list. An error or a
        // still-empty payload is not proof the account was removed.
        const accountsSettled = accountsQuery.isSuccess && !accountsQuery.isError;
        const starMissing = accountsSettled
            && starredId
            && rawAccounts.length > 0
            && !findStarredAccount(rawAccounts, appSettings);
        if (starMissing && user?.token) {
            delete appSettings.starredAccountId;
            delete appSettings.starredAccountPaper;
            updateAppSettings(user.token, {
                starredAccountId: undefined,
                starredAccountPaper: undefined,
            }).catch(() => {});
        }

        // Set initial account if needed, or replace bootstrap object with full account (includes AccountType)
        if (accountsList.length > 0) {
            const currentAccountId = selectedAccount?.AccountID;
            const fullAccountInList = currentAccountId && accountsList.find(acc => acc.AccountID === currentAccountId);
            // Star wins only when nothing is selected yet (app load). After that,
            // last-used selectedAccountId stays put so a star click does not switch accounts.
            const applyingStar = !selectedAccount && !!findStarredAccount(accountsList, appSettings);
            const preferredId = appSettings.selectedAccountId;
            const preferred = pickPreferredAccount({
                accounts: accountsList,
                appSettings,
                manualSelection: !!selectedAccount,
            });
            // Preferred id from settings may be stale after disconnect/reconnect — never keep it.
            // A starred default replaces that id on load, so it is not a stale selection.
            const preferredIsStale = !applyingStar && !!preferredId && !preferred;
            // Bootstrap object has AccountID + isPaperTrading but no AccountType / broker stamps.
            const isBootstrap =
              selectedAccount &&
              selectedAccount.AccountID &&
              (!selectedAccount.AccountType || !hasResolvableBrokerIdentity(selectedAccount));
            const selectedMissing = currentAccountId && !fullAccountInList;
            const newAccount = preferred || fullAccountInList || accountsList[0];
            if (!newAccount) return;

            const sameId = selectedAccount?.AccountID === newAccount.AccountID;
            const needsHydration = isBootstrap && sameId && (
              (!!newAccount.AccountType && !selectedAccount.AccountType) ||
              (hasResolvableBrokerIdentity(newAccount) && !hasResolvableBrokerIdentity(selectedAccount))
            );
            const shouldReplace =
              !selectedAccount ||
              !sameId ||
              preferredIsStale ||
              selectedMissing ||
              needsHydration;

            let nextApp = appSettings;
            let accountPatch = null;
            if (shouldReplace) {
                setSelectedAccount(newAccount);
                const starOverridesLastUsed = applyingStar && (
                    String(appSettings.selectedAccountId) !== String(newAccount.AccountID) ||
                    !!appSettings.isPaperTrading !== !!newAccount.isPaperTrading
                );
                // Persist correction when settings pointed at a removed account,
                // and when the starred default should replace last-used on load.
                if ((preferredIsStale || selectedMissing || starOverridesLastUsed) && newAccount?.AccountID && user?.token) {
                    accountPatch = {
                        selectedAccountId: newAccount.AccountID,
                        isPaperTrading: newAccount.isPaperTrading,
                    };
                    nextApp = { ...appSettings, ...accountPatch };
                }
            }

            // First time we see broker accounts, check the account the summary
            // will use. Later checkbox edits are left alone.
            const savedRundownKeys = nextApp.pushNotifications?.rundownAccountKeys;
            const rundownExplicitlyEmpty = Array.isArray(savedRundownKeys) && savedRundownKeys.length === 0;
            const knownRundownKeys = new Set(accountsList.map(rundownAccountKey).filter(Boolean));
            const rundownStillValid = Array.isArray(savedRundownKeys)
              && savedRundownKeys.some((key) => knownRundownKeys.has(String(key)));
            let rundownKeyToSave = null;
            if (!rundownExplicitlyEmpty && !rundownStillValid && user?.token) {
                const fallback = findStarredAccount(accountsList, nextApp)
                  || newAccount
                  || accountsList.find((account) => !account.isPaperTrading)
                  || accountsList[0];
                const rundownKey = rundownAccountKey(fallback);
                if (rundownKey) {
                    rundownKeyToSave = rundownKey;
                    nextApp = {
                        ...nextApp,
                        pushNotifications: {
                            ...(nextApp.pushNotifications || {}),
                            rundownAccountKeys: [rundownKey],
                        },
                    };
                }
            }
            if ((accountPatch || rundownKeyToSave) && user?.token) {
                updateAppSettings(user.token, (latest) => {
                    const patch = { ...(accountPatch || {}) };
                    if (rundownKeyToSave) {
                        patch.pushNotifications = {
                            ...(latest.pushNotifications || {}),
                            rundownAccountKeys: [rundownKeyToSave],
                        };
                    }
                    return patch;
                }).catch(() => {});
            }
        } else if (selectedAccount && !accountsQuery.isLoading) {
            // Accounts emptied after a settled fetch (e.g. disconnect) — clear selection
            setSelectedAccount(null);
        }
    }, [user?.token, isDemo, accountsQuery.isLoading, accountsQuery.isSuccess, accountsQuery.isError, accountsQuery.data, user?.appSettings?.selectedAccountId, user?.appSettings?.starredAccountId, user?.appSettings?.starredAccountPaper, user?.appSettings?.isPaperTrading, selectedAccount?.AccountID, selectedAccount?.AccountType, selectedAccount?.adapter_type, selectedAccount?.ct_connection_id, selectedAccount?.connection_id, selectedAccount?.broker_name, updateAppSettings]);

    // Update isPaperTrading based on selected account
    useEffect(() => {
        if (selectedAccount && selectedAccount.hasOwnProperty('isPaperTrading')) {
            setIsPaperTrading((prev) =>
                prev === selectedAccount.isPaperTrading ? prev : selectedAccount.isPaperTrading
            );
        }
    }, [selectedAccount?.AccountID, selectedAccount?.isPaperTrading]);

    // Keep activeBrokerConnection aligned with the selected account so MD/trade
    // nav and capability checks follow cross-broker account switches.
    useEffect(() => {
        if (!selectedAccount || !user?.token) return;
        const nextConn = resolveBrokerConnectionForAccount(user, selectedAccount);
        if (!nextConn) return;
        if (brokerConnectionsEqual(user.activeBrokerConnection, nextConn)) return;
        updateUserSettingsRef.current?.({ activeBrokerConnection: nextConn });
        void queryClient.invalidateQueries({ queryKey: ['broker', 'capabilities'] });
    }, [
        selectedAccount?.AccountID,
        selectedAccount?.ct_connection_id,
        selectedAccount?.connection_id,
        selectedAccount?.adapter_type,
        selectedAccount?.broker_name,
        selectedAccount?.institution_name,
        user?.token,
        user?.activeBrokerConnection,
        user?.brokerConnections,
        queryClient,
    ]);

    // Balances, Orders, Positions via React Query keyed by account
    // Only enable queries when both account and paper trading mode are consistent.
    // TS Direct balance REST poll runs only while positions are open (see useBalancesQuery).
    const hasActivePositions = (positionsManager.positions?.length ?? 0) > 0;
    const balancesQuery = useBalancesQuery(isPaperTrading, selectedAccount, { hasActivePositions });
    const ordersQuery = useOrdersQuery(isPaperTrading, selectedAccount);
    const positionsQuery = usePositionsQuery(isPaperTrading, selectedAccount);
    // Defer net-worth live-account GETs until selected balances settle so they don't
    // compete with the hot-path CT balances/positions round-trips.
    const selectedBalancesSettled =
      !selectedAccount?.AccountID ||
      balancesQuery.isSuccess ||
      balancesQuery.isError;
    const { netWorth, breakdown: netWorthBreakdown, isLoading: netWorthLoading, hasLiveAccounts } = useNetWorthQuery(
      accounts,
      { deferUntilReady: !selectedBalancesSettled }
    );
    // Loads the first page of historical orders. The orders table requests older pages.
    useOrdersManager();

    // Track loading states for balances, orders, and positions
    const [balancesLoading, setBalancesLoading] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [positionsLoading, setPositionsLoading] = useState(false);
    // Positions: cleared on account switch → first REST/balances seed replaces → later merges with streams
    const lastPositionsAccountRef = useRef(null);
    const [positionsHydrated, setPositionsHydrated] = useState(false);

    useEffect(() => {
        setBalancesLoading(balancesQuery.isLoading);
        if (balancesQuery.data?.Balances) setBalances(balancesQuery.data.Balances);
        // Balances cold-miss may include Positions (CT Equity seed). Hydrate the table
        // once so it does not stay spinning until a separate positions GET returns.
        // Later balance refetches — including the one fired when a position closes —
        // must not merge that snapshot back over the stream book. The snapshot is often
        // still the pre-close row, which is why a closed position intermittently stuck.
        const seeded = balancesQuery.data?.Positions;
        if (Array.isArray(seeded) && selectedAccount?.AccountID) {
            if (lastPositionsAccountRef.current !== selectedAccount.AccountID) {
                lastPositionsAccountRef.current = selectedAccount.AccountID;
                positionsManager.replacePositions(seeded);
                setPositionsLoading(false);
            }
        }
    }, [
        balancesQuery.isLoading,
        balancesQuery.data,
        selectedAccount?.AccountID,
        positionsManager.replacePositions,
    ]);

    useEffect(() => {
        setOrdersLoading(ordersQuery.isLoading);
    }, [ordersQuery.isLoading]);

    useEffect(() => {
        if (!positionsQuery.isLoading) {
            setPositionsLoading(false);
            return;
        }
        // Don't re-assert loading if balances already seeded rows into the manager.
        setPositionsLoading((prev) => (prev === false ? false : true));
    }, [positionsQuery.isLoading]);

    // Combined loading: wait only until REST balances for the selected account exist.
    // Positions / streams refine P&L in the background and must not block the card.
    const isAccountDataLoading = useMemo(() => {
        if (accountsQuery.isLoading) return true;
        if (!selectedAccount?.AccountID) return false;
        const hasBalance = Array.isArray(balances)
          && balances.some((b) => b.AccountID === selectedAccount.AccountID);
        if (hasBalance) return false;
        return balancesQuery.isLoading || balancesQuery.isFetching;
    }, [
        accountsQuery.isLoading,
        selectedAccount?.AccountID,
        balances,
        balancesQuery.isLoading,
        balancesQuery.isFetching,
    ]);
    useEffect(() => {
        const newOrders = ordersQuery.data?.Orders;
        if (!newOrders) return;
        setOrders((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) {
                return newOrders;
            }
            // Merge by OrderID, letting latest regular orders override existing entries (including historical)
            const byId = new Map(prev.map(o => [String(o.OrderID), o]));
            newOrders.forEach(o => {
                if (o?.OrderID == null) return;
                const replacedId = retireLinkedOrder(o);
                if (replacedId) {
                    const old = byId.get(replacedId);
                    byId.set(replacedId, {
                        ...(old || { OrderID: replacedId }),
                        Status: 'CAN',
                        ClosedDateTime: old?.ClosedDateTime || new Date().toISOString(),
                    });
                }
                byId.set(String(o.OrderID), sealTerminalOrder(o));
            });
            const merged = Array.from(byId.values());
            merged.sort((a, b) => new Date(b?.OpenedDateTime || 0) - new Date(a?.OpenedDateTime || 0));
            return merged;
        });
    }, [ordersQuery.data]);

    useEffect(() => {
        const accountId = selectedAccount?.AccountID;
        const newPositions = positionsQuery.data?.Positions;
        if (!accountId || !Array.isArray(newPositions)) return;

        if (lastPositionsAccountRef.current !== accountId) {
            lastPositionsAccountRef.current = accountId;
            positionsManager.replacePositions(newPositions);
            setPositionsHydrated(true);
            return;
        }
        positionsManager.mergeQueryPositions(newPositions);
        setPositionsHydrated(true);
    }, [
        positionsQuery.data,
        selectedAccount?.AccountID,
        positionsManager.replacePositions,
        positionsManager.mergeQueryPositions,
    ]);

    // Process ticker options from user settings (already fetched)
    useEffect(() => {
        if (user?.tickerOptions?.suggestions) {
            const tickerValues = user.tickerOptions.suggestions
                .filter(suggestion => suggestion.type === 'futures' || suggestion.type === 'stock') 
                .map(suggestion => suggestion.value);
            setTickers(tickerValues);
        } else if (user) {
            // Fallback to default tickers if not in settings
            setTickers(['MESZ24', 'MYMZ24', 'MNQZ24', 'M2KZ24']);
        }
    }, [user?.tickerOptions]);

    // NOTE: Ticker details fetching and isFutures calculation moved to a custom hook
    // Components should use useSelectedTickerDetails hook which consumes both contexts
    const isFutures = useMemo(() => {
        const assetType = selectedTickerDetails?.AssetType?.toUpperCase?.();
        const securityType = selectedTickerDetails?.SecurityType?.toLowerCase?.();
        if (assetType === 'FUTURE') return true;
        if (typeof securityType === 'string' && securityType.includes('future')) return true;
        // Without selectedTicker, we can only rely on selectedTickerDetails
        return false;
    }, [selectedTickerDetails])

    // Function to refresh only balances (lighter refresh)
    const refreshBalancesOnly = useCallback(async () => {
        // Let React Query refetch balances for current account
        await balancesQuery.refetchBalances?.({ force: true });
    }, [balancesQuery]);

    // Mobile browsers suspend sockets when the app is backgrounded. On return, pull a
    // fresh positions snapshot so the cache matches the broker. Desktop stays on the
    // stream: a visibility refetch there used to race and duplicate rows.
    const isMobile = useIsMobile(768, 1024);
    const refetchPositionsRef = useRef(positionsQuery.refetch);
    useEffect(() => { refetchPositionsRef.current = positionsQuery.refetch; }, [positionsQuery.refetch]);
    const selectedAccountIdRef = useRef(selectedAccount?.AccountID);
    useEffect(() => { selectedAccountIdRef.current = selectedAccount?.AccountID; }, [selectedAccount?.AccountID]);

    useEffect(() => {
        if (!isMobile) return undefined;
        let timer = null;
        const scheduleRefetch = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                if (!selectedAccountIdRef.current || !userRef.current?.token) return;
                void refetchPositionsRef.current?.();
            }, 250);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') scheduleRefetch();
        };
        const onPageShow = (event) => {
            if (event?.persisted) scheduleRefetch();
        };
        window.addEventListener('focus', scheduleRefetch);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pageshow', onPageShow);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('focus', scheduleRefetch);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pageshow', onPageShow);
        };
    }, [isMobile]);

    /**
     * Centralized handler for account changes.
     * 1. Clear local orders / balances / positions (no leftover rows)
     * 2. Kick REST refetch for balances + positions immediately (async)
     * 3. Streams restart asynchronously via StreamsContext streamEpoch
     * 4. Persist selected account to app settings
     */
    const setSelectedAccountPersist = useCallback((account) => {
        setOrders([]);
        setBalances([]);
        positionsManager.clearPositions();
        lastPositionsAccountRef.current = null;
        setPositionsHydrated(false);

        // Sync paper mode in the same turn so balances/positions queries enable immediately
        const paperTrading = account && Object.prototype.hasOwnProperty.call(account, 'isPaperTrading')
            ? !!account.isPaperTrading
            : undefined;
        if (paperTrading !== undefined) {
            setIsPaperTrading(paperTrading);
        }

        setSelectedAccount(account);

        // REST first — do not await; card fills when balances land. Streams warm up separately.
        const currentUser = userRef.current;
        const accountId = account?.AccountID;
        if (currentUser?.token && accountId && paperTrading !== undefined) {
            const balanceKey = queryKeys.balances(paperTrading, accountId);
            const positionsKey = queryKeys.positions(paperTrading, accountId);
            void queryClient.invalidateQueries({ queryKey: balanceKey });
            void queryClient.invalidateQueries({ queryKey: positionsKey });
            void queryClient.refetchQueries({ queryKey: balanceKey });
            void queryClient.refetchQueries({ queryKey: positionsKey });
        }

        // Sync broker context + capabilities immediately (don't wait for effect)
        const nextConn = resolveBrokerConnectionForAccount(currentUser, account);
        if (nextConn && !brokerConnectionsEqual(currentUser?.activeBrokerConnection, nextConn)) {
            updateUserSettingsRef.current?.({ activeBrokerConnection: nextConn });
        }
        void queryClient.invalidateQueries({ queryKey: ['broker', 'capabilities'] });

        try {
            if (currentUser?.token && accountId) {
                updateAppSettings(currentUser.token, {
                    selectedAccountId: accountId,
                    isPaperTrading: account.isPaperTrading,
                })
                    .then(result => {
                        if (result?.error || result?.Message) {
                            console.warn('[TradingContext] Failed to persist selectedAccount to server:', result);
                        }
                    })
                    .catch(err => console.warn('[TradingContext] Failed to persist selectedAccount to server:', err));
            }
        } catch (err) {
            console.warn('[TradingContext] setSelectedAccountPersist error:', err);
        }
    }, [updateAppSettings, positionsManager.clearPositions, queryClient]);

    /**
     * Reset all account-scoped trading state when a broker is disconnected. Without this, the
     * last broker's balances/positions linger in local state and the React Query cache, producing
     * `[PLSummary] Balance data does not match selected account` spam (stale balance AccountID vs a
     * now-null selectedAccount). Clears local state, drops cached account data, and unselects.
     */
    const clearTradingStateOnDisconnect = useCallback(() => {
        setOrders([]);
        setBalances([]);
        positionsManager.clearPositions();
        setPositionsHydrated(false);
        setSelectedAccount(null);
        try {
            queryClient.removeQueries({ queryKey: ['balances'] });
            queryClient.removeQueries({ queryKey: ['orders'] });
            queryClient.removeQueries({ queryKey: ['positions'] });
            queryClient.removeQueries({ queryKey: ['accounts'] });
        } catch (_) {}
    }, [queryClient, positionsManager.clearPositions]);

    return useMemo(() => ({
        ...defaultUseTrading,
        balances,
        setBalances,
        clearTradingStateOnDisconnect,
        isPaperTrading,
        isFutures,
        setIsPaperTrading: setPersistedIsPaperTrading,
        // NOTE: selectedTicker removed - get it from AppStateContext instead
        selectedTickerDetails,
        setSelectedTickerDetails,
        selectedAccount,
        setSelectedAccount: setSelectedAccountPersist,
        currentStopLossOrders,
        setCurrentStopLossOrders,
        accounts,
        // Total net worth (sum of Equity) across live/non-paper accounts only
        netWorth,
        netWorthBreakdown,
        netWorthLoading,
        hasLiveAccounts,
        orders,
        // Positions managed by centralized positions manager
        positions: positionsManager.positions,
        positionsHydrated,
        isLoading,
        // Loading states for individual queries
        balancesLoading,
        ordersLoading,
        positionsLoading,
        // Combined loading state for account data (accounts + balances + positions)
        isAccountDataLoading,
        // Ticker data
        tickers,
        // Data refresh functions
        refreshBalancesOnly,
        setOrders,
        // Position manager actions (for use by streams and other components)
        positionsManager
    }), [
        balances,
        clearTradingStateOnDisconnect,
        isPaperTrading,
        isFutures,
        setPersistedIsPaperTrading,
        selectedTickerDetails,
        selectedAccount,
        setSelectedAccountPersist,
        currentStopLossOrders,
        accounts,
        netWorth,
        netWorthBreakdown,
        netWorthLoading,
        hasLiveAccounts,
        orders,
        positionsHydrated,
        positionsManager,
        isLoading,
        balancesLoading,
        ordersLoading,
        positionsLoading,
        isAccountDataLoading,
        tickers,
        refreshBalancesOnly,
        setOrders,
    ]);
}

export const TradingContext = createContext(defaultUseTrading);

export const TradingContextProvider = ({ children }) => (
    <TradingContext.Provider value={useTradingContext()}>
        {children}
    </TradingContext.Provider>
)